import {
  createTheme,
  inputClasses,
  Theme,
  ThemeOptions,
  ThemeProvider
} from '@mui/material';
import merge from '@mui/system/merge';
import { usePortalMiddlewareGetWhiteLabelUnauthorized } from '@vestwell-unified-api/hooks';

import { FC, memo, ReactNode, useMemo } from 'react';

import { VestwellPalette } from './global';

export type MaterialThemeProviderProps = {
  children: ReactNode;
  isWhitelabeled?: boolean;
  themeOverrides?: Partial<Theme>;
};

export const palette: Partial<VestwellPalette> = {
  black: {
    contrastText: '#FFFFFF',
    main: '#000000'
  },
  blue100: {
    contrastText: '#FFFFFF',
    main: '#00131C'
  },
  blue20: {
    contrastText: '#000000',
    main: '#DCF1FA'
  },
  blue30: {
    contrastText: '#000000',
    main: '#ADDAF0'
  },
  blue50: {
    contrastText: '#000000',
    main: '#1595D1'
  },
  blue70: {
    contrastText: '#FFFFFF',
    main: '#1F5670'
  },
  blue90: {
    contrastText: '#FFFFFF',
    main: '#0D2936'
  },
  border: {
    contrastText: '#000000',
    main: '#D4D4D4'
  },
  error: {
    contrastText: '#FFF0F0',
    dark: '#800017',
    light: '#ED8C8C',
    main: '#E00028'
  },
  gold10: {
    contrastText: '#000000',
    main: '#FFFCEB'
  },
  gold100: {
    contrastText: '#FFFFFF',
    main: '#1F1A00'
  },
  gold20: {
    contrastText: '#000000',
    main: '#F9F2C8'
  },
  gold30: {
    contrastText: '#000000',
    main: '#E9DA84'
  },
  gold50: {
    contrastText: '#000000',
    main: '#C3A322'
  },
  gold70: {
    contrastText: '#FFFFFF',
    main: '#725F18'
  },
  gold90: {
    contrastText: '#FFFFFF',
    main: '#3D320C'
  },
  gray20: {
    contrastText: '#000000',
    main: '#EDEDED'
  },
  gray50: {
    contrastText: '#000000',
    main: '#8A8A8A'
  },
  gray90: {
    contrastText: '#FFFFFF',
    main: '#262626'
  },
  green100: {
    contrastText: '#FFFFFF',
    main: '#00140D'
  },
  green20: {
    contrastText: '#000000',
    main: '#D0F5E8'
  },
  green30: {
    contrastText: '#000000',
    main: '#94E3C7'
  },
  green50: {
    contrastText: '#000000',
    main: '#109C6B'
  },
  green70: {
    contrastText: '#FFFFFF',
    main: '#095C3F'
  },
  green90: {
    contrastText: '#FFFFFF',
    main: '#022E1F'
  },
  gustoOrange: {
    contrastText: '#FFFFFF',
    main: '#F45D48'
  },
  highlight: {
    contrastText: '#000000',
    dark: '#3D2A8C',
    light: '#AF9EF0',
    main: '#7258E0'
  },
  info: {
    contrastText: '#F2FBFF',
    dark: '#1B3A4A',
    light: '#65BCE5',
    main: '#0C78AB'
  },
  neutral: {
    contrastText: '#FAFAFA',
    dark: '#363636',
    light: '#B3B3B3',
    main: '#6E6E6E'
  },
  onPayBlue: {
    contrastText: '#FFFFFF',
    main: '#054296'
  },
  orange100: {
    contrastText: '#FFFFFF',
    main: '#1F0C00'
  },
  orange20: {
    contrastText: '#000000',
    main: '#FAEAE1'
  },
  orange30: {
    contrastText: '#000000',
    main: '#EDCEBB'
  },
  orange50: {
    contrastText: '#000000',
    main: '#DB6521'
  },
  orange70: {
    contrastText: '#FFFFFF',
    main: '#7A4323'
  },
  orange90: {
    contrastText: '#FFFFFF',
    main: '#3D1E0C'
  },
  primary: {
    contrastText: '#FFFFFF',
    dark: '#1B3A4A',
    light: '#65BCE5',
    main: '#0C78AB'
  },
  purple100: {
    contrastText: '#FFFFFF',
    main: '#170D40'
  },
  purple20: {
    contrastText: '#000000',
    main: '#E2DCFA'
  },
  purple30: {
    contrastText: '#000000',
    main: '#CEC4F5'
  },
  purple50: {
    contrastText: '#000000',
    main: '#8D77E5'
  },
  purple70: {
    contrastText: '#FFFFFF',
    main: '#523BB2'
  },
  purple90: {
    contrastText: '#FFFFFF',
    main: '#2D2161'
  },
  quickbooksGreen: {
    contrastText: '#FFFFFF',
    main: '#2CA01C'
  },
  red100: {
    contrastText: '#FFFFFF',
    main: '#38000A'
  },
  red20: {
    contrastText: '#000000',
    main: '#F7DADF'
  },
  red30: {
    contrastText: '#000000',
    main: '#F2BFBF'
  },
  red50: {
    contrastText: '#000000',
    main: '#E55555'
  },
  red70: {
    contrastText: '#FFFFFF',
    main: '#A6001E'
  },
  red90: {
    contrastText: '#FFFFFF',
    main: '#5E0011'
  },
  secondary: {
    contrastText: '#FFFFFF',
    dark: '#544614',
    light: '#DDC04A',
    main: '#8F7313'
  },
  success: {
    contrastText: '#E5FFF6',
    dark: '#043D29',
    light: '#4CC79C',
    main: '#098056'
  },
  text: {
    disabled: '#6E6E6E',
    primary: '#121212',
    secondary: '#4F4F4F'
  },
  warning: {
    contrastText: '#FFF9F5',
    dark: '#542C14',
    light: '#E8A37B',
    main: '#B84F12'
  },
  white: {
    contrastText: '#000000',
    main: '#FFFFFF'
  }
};

export const theme = createTheme({
  components: {
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          '&.Mui-expanded': {
            margin: 0
          },
          margin: 0
        }
      }
    },
    MuiAlert: {
      defaultProps: {
        'data-component': 'alert'
      },
      styleOverrides: {
        message: {
          '& a': {
            color: palette.black.main,
            textDecoration: 'underline'
          },
          '& button.MuiButton-inline': {
            color: palette.black.main,
            textDecoration: 'underline'
          }
        },
        root: {
          borderRadius: '7px',
          color: palette.neutral.dark,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          marginTop: '1rem',
          padding: '1rem auto',
          //the same as navbar
          zIndex: 120
        }
      }
    },
    MuiAutocomplete: {
      defaultProps: {
        'data-component': 'autocomplete'
      },
      styleOverrides: {
        input: ({ theme }) => ({
          padding: theme.spacing(1, 2)
        })
      }
    },
    MuiAvatar: {
      defaultProps: {
        'data-component': 'avatar'
      }
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          boxShadow: '0 0 #0000, 0 0 #0000, 0px 0px 10px #72727226',
          height: 64
        }
      }
    },
    MuiBottomNavigationAction: {
      defaultProps: {
        focusRipple: false
      },
      styleOverrides: {
        root: {
          color: palette.black.main,
          height: '4rem',
          maxWidth: 'unset',
          minWidth: 'unset',
          padding: 0
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableFocusRipple: true
      },
      styleOverrides: {
        contained: {
          boxShadow: 'none',
          fontSize: '0.875rem',
          fontWeight: 700,
          lineHeight: '1.5',
          paddingLeft: '1rem',
          paddingRight: '1rem'
        },
        inline: {
          '&:hover': {
            backgroundColor: 'transparent'
          },
          fontWeight: 700,
          lineHeight: 'inherit',
          minWidth: 0,
          padding: 0,
          width: 'fit-content'
        },
        outlined: ({ ownerState, theme }) => ({
          border: `1px solid ${
            ownerState.color?.includes('.')
              ? theme.palette[ownerState.color?.split('.')[0]][
                  ownerState.color?.split('.')[1]
                ]
              : theme.palette[ownerState.color]?.main ||
                theme.palette.primary.main
          }`,
          fontSize: '0.875rem',
          fontWeight: 700,
          lineHeight: '1.5',
          paddingLeft: '1rem',
          paddingRight: '1rem'
        }),
        root: ({ ownerState, theme }) => ({
          '& .MuiCircularProgress-root': {
            color: theme.palette.primary.main
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.gray20.main,
            borderColor: theme.palette.border.main,
            color: theme.palette.neutral.main
          },
          '&:focus-visible': {
            outline: `1px solid ${
              ownerState.color?.includes('.')
                ? theme.palette[ownerState.color?.split('.')[0]][
                    ownerState.color?.split('.')[1]
                  ]
                : theme.palette[ownerState.color]?.main ||
                  theme.palette.primary.main
            }`,
            outlineOffset: '2px'
          },
          borderRadius: '0.375rem',
          borderWidth: 1,
          fontSize: '0.875rem',
          fontWeight: 700,
          lineHeight: '1.5',
          marginBottom: 0,
          textTransform: 'unset'
        }),
        text: {
          fontWeight: 700,
          lineHeight: '1.5',
          paddingLeft: '1rem',
          paddingRight: '1rem'
        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        'data-component': 'buttonBase'
      },
      styleOverrides: {
        root: {
          '&.MuiAccordionSummary-root.Mui-expanded': {
            minHeight: '48px'
          }
        }
      }
    },
    MuiButtonGroup: {
      defaultProps: {
        disableFocusRipple: true
      }
    },
    MuiCard: {
      defaultProps: {
        'data-component': 'card'
      },
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
          padding: '16px'
        }
      }
    },
    MuiCardContent: {
      defaultProps: {
        'data-component': 'cardContent'
      },
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: 0
          },
          padding: 0
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        'data-component': 'cardHeader'
      },
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0
        }
      }
    },
    MuiCheckbox: {
      defaultProps: {
        disableFocusRipple: true
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          '&.Mui-focusVisible': {
            outline: `1px solid ${
              ownerState.color?.includes('.')
                ? theme.palette[ownerState.color?.split('.')[0]][
                    ownerState.color?.split('.')[1]
                  ]
                : theme.palette[ownerState.color]?.main ||
                  theme.palette.primary.main
            }`,
            outlineOffset: '2px'
          },
          borderRadius: '5px',
          margin: '0 9px',
          padding: 0
        })
      }
    },
    MuiCssBaseline: {
      styleOverrides: theme => ({
        '.bg-primary': {
          backgroundColor: theme.palette.primary.main
        },
        '.border-primary': {
          borderColor: theme.palette.primary.main
        },
        //TODO: return to it once Form is refactored
        '.contents': {
          display: 'contents'
        },
        '.list-disc': {
          listStyleType: 'disc'
        },
        '.list-inside': {
          listStylePosition: 'inside'
        },
        '.list-outside': {
          listStylePosition: 'outside'
        },
        '.sr-only': {
          borderWidth: 0,
          clip: 'rect(0, 0, 0, 0)',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1px'
        },
        '.text-primary': {
          color: theme.palette.primary.main
        },
        'a:focus-visible': {
          borderRadius: '5px',
          outline: `1px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px'
        },
        'a:not(.MuiButtonBase-root)': {
          fontWeight: 700
        }
      })
    },
    MuiDataGrid: {
      defaultProps: {
        'data-component': 'dataGrid'
      },
      styleOverrides: {
        cell: ({ theme }) => ({
          '&[aria-invalid="true"].MuiDataGrid-cell--editing:focus-within': {
            outline: 'none'
          },
          '&[aria-invalid="true"].MuiDataGrid-withBorderColor': {
            // todo:: find a way to better blend this into the palette
            backgroundColor: '#FEF8FF',
            border: `solid ${theme.palette.error.main} 1px`,
            outline: 'none'
          }
        }),
        editInputCell: ({ theme }) => ({
          '& input': {
            color: theme.palette.primary.main,
            fontSize: 'initial',
            padding: theme.spacing(0, 1)
          },
          height: '100%',
          margin: 0
        }),
        overlayWrapperInner: {
          display: 'flex',
          flexDirection: 'column'
        },
        root: ({ theme }) => ({
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
            display: 'flex',
            fontSize: '1rem',
            lineHeight: 1.43,
            padding: '0.25rem'
          },
          '& .MuiDataGrid-cell--editable': {
            cursor: 'cell'
          },
          '& .MuiDataGrid-cell--editable > input': {
            padding: '0.25rem !important'
          },
          '& .MuiDataGrid-cell--textRight.MuiDataGrid-cell--editable > input': {
            textAlign: 'right'
          },
          '& .MuiDataGrid-columnHeader': {
            color: theme.palette.neutral.dark,
            fontSize: '0.6875rem',
            fontWeight: 800,
            letterSpacing: '0.07em',
            lineHeight: 1.5,
            padding: '0 0.25rem',
            textTransform: 'uppercase'
          },
          '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
            visibility: 'visible',
            width: 'auto'
          },
          '& .MuiDataGrid-columnHeader--alignRight': {
            textAlign: 'right'
          },
          '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon':
            {
              opacity: '1 !important'
            },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 800
          },
          '& .MuiDataGrid-columnHeaders': {
            height: '100%'
          },
          '& .MuiDataGrid-columnHeadersInner': {
            alignItems: 'center',
            height: 'inherit'
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          },
          '& .MuiDataGrid-container--top [role=row]': {
            backgroundColor: 'inherit'
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: theme.palette.info.contrastText
          },
          '& .MuiIconButton-root': {
            color: theme.palette.neutral.main
          },
          '& .MuiIconButton-root:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.primary.main
          },
          '& .MuiTablePagination-displayedRows': {
            color: theme.palette.text.secondary,
            fontSize: '1rem',
            marginBottom: 0
          },
          '& .MuiTablePagination-selectLabel': {
            color: theme.palette.text.secondary,
            fontSize: '1rem',
            marginBottom: 0
          },
          '&.MuiDataGrid-row--editing': {
            boxShadow: 'none'
          },
          '--DataGrid-rowBorderColor': theme.palette.gray20.main,
          border: 0,
          color: theme.palette.text.secondary
        })
      }
    },
    MuiIconButton: {
      defaultProps: {
        disableFocusRipple: true
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          '&:focus-visible': {
            outline: `1px solid ${
              ownerState.color?.includes('.')
                ? theme.palette[ownerState.color?.split('.')[0]][
                    ownerState.color?.split('.')[1]
                  ]
                : theme.palette[ownerState.color]?.main ||
                  theme.palette.primary.main
            }`,
            outlineOffset: '2px'
          }
        }),
        sizeSmall: {
          '& > svg': {
            height: '0.75em',
            width: '0.75em'
          },
          height: '0.75em',
          width: '0.75em'
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        input: {
          textAlign: 'inherit'
        },
        root: ({ ownerState, theme }) => ({
          '& input::placeholder': {
            color: theme.palette.neutral.main,
            opacity: 1
          },
          '&:after': {
            borderBottom: 0
          },
          '&:before': {
            borderBottom: `1px dashed ${
              ownerState['data-no-focus-style']
                ? 'transparent !important'
                : ownerState.disabled
                  ? theme.palette.border.main
                  : theme.palette.neutral.main
            }`
          },
          [`label+&.${inputClasses.root}`]: {
            marginTop: 0
          },
          [`&:hover:not(.${inputClasses.disabled}, .${inputClasses.error}, .${inputClasses.focused}):before`]:
            {
              borderBottom: ownerState['data-no-focus-style']
                ? `1px dashed transparent !important`
                : `1px dashed ${theme.palette.primary.main}`
            },
          [`&.${inputClasses.focused}:hover:before`]: {
            borderBottom: 0
          },
          [`&.${inputClasses.focused}`]: {
            outline: ownerState['data-no-focus-style']
              ? 'none'
              : `1px solid ${theme.palette.primary.main}`
          },
          [`&.${inputClasses.focused}:before`]: {
            borderBottom: 0
          },
          [`&.${inputClasses.focused}:after`]: {
            borderBottom: 0
          },
          backgroundColor:
            theme.palette[ownerState.disabled ? 'gray20' : 'white'].main,
          borderRadius: theme.spacing(0.5),
          color: theme.palette[ownerState.disabled ? 'gray50' : 'primary'].main,
          marginBottom: 0,
          padding: theme.spacing(1, 2)
        })
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: palette.text.secondary,
          fontWeight: 700,
          marginBottom: 0,
          position: 'relative',
          transform: 'none',
          whiteSpace: 'inherit',
          zIndex: 0
        }
      }
    },
    MuiList: {
      defaultProps: {
        'data-component': 'list'
      }
    },
    MuiListItem: {
      defaultProps: {
        'data-component': 'listItem'
      }
    },
    MuiListItemButton: {
      defaultProps: {
        'data-component': 'listItemButton'
      }
    },
    MuiListSubheader: {
      defaultProps: {
        'data-component': 'listSubheader'
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            '&:focus': {
              backgroundColor: theme.palette.neutral.contrastText
            },
            '&:hover': {
              backgroundColor: theme.palette.neutral.contrastText
            },
            backgroundColor: theme.palette.white.main,
            color: theme.palette.primary.main,
            fontWeight: 700
          },
          '&:focus': {
            backgroundColor: theme.palette.neutral.contrastText,
            borderLeft: `4px solid ${theme.palette.primary.main}`
          },
          '&:hover': {
            backgroundColor: theme.palette.neutral.contrastText
          },
          backgroundColor: theme.palette.white.main,
          borderBottomColor: theme.palette.border.main,
          borderBottomStyle: 'dashed',
          borderBottomWidth: 1,
          borderLeft: '4px solid transparent',
          color: theme.palette.text.secondary,
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: '1.5',
          margin: 0,
          outline: 'none',
          padding: 8,
          userSelect: 'none'
        })
      }
    },
    MuiModal: {
      defaultProps: {
        'data-component': 'modal'
      }
    },
    MuiPaper: {
      defaultProps: {
        'data-component': 'paper'
      },
      styleOverrides: {
        elevation0: ({ theme }) => ({
          '&.MuiCard-root': {
            border: `${theme.spacing(0.25)} solid ${theme.palette.border.main}`
          }
        }),
        // todo:: temporary workaround until we can get a valid shadow list from ux
        elevation1: ({ theme }) => ({
          '&.MuiCard-root': {
            boxShadow: theme.shadows[24]
          }
        })
      }
    },
    MuiPopper: {
      defaultProps: {
        'data-component': 'popper'
      }
    },
    MuiRadio: {
      defaultProps: {
        disableFocusRipple: true
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          '&.Mui-focusVisible': {
            outline: `1px solid ${
              ownerState.color?.includes('.')
                ? theme.palette[ownerState.color?.split('.')[0]][
                    ownerState.color?.split('.')[1]
                  ]
                : theme.palette[ownerState.color]?.main ||
                  theme.palette.primary.main
            }`,
            outlineOffset: '2px'
          },
          padding: 0
        })
      }
    },
    MuiSkeleton: {
      defaultProps: {
        'data-component': 'skeleton'
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeLarge: {
          height: '1.2em',
          width: '1.2em'
        },
        fontSizeSmall: {
          height: '0.85em',
          width: '0.85em'
        },
        // This is a workaround to have ability to apply nested colors to MUI's Icons
        root: ({ ownerState, theme }) => {
          const colorPath = ownerState.color?.split('.') || [];
          let color = theme.palette;

          colorPath.forEach(key => {
            if (color[key]) {
              color = color[key];
            }
          });

          return {
            color: typeof color === 'string' ? color : undefined
          };
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: () => ({
          color: palette.neutral.dark,
          fontSize: '0.6875rem',
          fontWeight: 800,
          letterSpacing: '0.07em',
          lineHeight: 1.5,
          textTransform: 'uppercase'
        }),
        root: {
          '&[role="rowheader"]': {
            fontWeight: 600
          },
          ':first-of-type': {
            paddingLeft: '0.25rem'
          },
          ':last-of-type': {
            paddingRight: '0.25rem'
          },
          fontSize: '1rem',
          padding: '0.6875rem'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          ':last-child:not(.MuiTableRow-head) .MuiTableCell-root': {
            borderBottom: '1px solid transparent'
          }
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: '1.5',
          padding: '0.5rem 0.25rem',
          width: '3rem'
        }
      }
    },
    MuiToolbar: {
      defaultProps: {
        'data-component': 'toolbar'
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiTablePagination-actions': {
            color: theme.palette.text.secondary,
            fontSize: '1rem',
            marginBottom: 0
          },
          '& .MuiTablePagination-displayedRows': {
            color: theme.palette.text.secondary,
            fontSize: '1rem',
            marginBottom: 0
          },
          '& .MuiTablePagination-selectLabel': {
            color: theme.palette.text.secondary,
            fontSize: '1rem',
            marginBottom: 0
          }
        })
      }
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          '&::before': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
          },
          color: palette.white.main
        },
        tooltip: {
          backgroundColor: palette.white.main,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
          color: palette.neutral.main,
          fontSize: '0.875rem',
          fontWeight: 400,
          padding: '0.75rem'
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        'data-component': 'text'
      }
    },
    Navbar: {
      styleOverrides: {
        desktop: {
          selected: 'rgb(250,250,250)',
          unselected: 'rgb(242,242,242)'
        },
        mobile: {
          selected: '#153850FF',
          unselected: 'rgb(250,250,250)'
        }
      }
    }
  },
  palette,
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(114, 114, 114, 0.15),0px 1px 1px 0px rgba(114, 114, 114, 0.15),0px 1px 3px 0px rgba(114, 114, 114, 0.15)',
    '0px 3px 3px -2px rgba(114, 114, 114, 0.15),0px 3px 4px 0px rgba(114, 114, 114, 0.15),0px 1px 8px 0px rgba(114, 114, 114, 0.15)',
    '0px 3px 1px -2px rgba(114, 114, 114, 0.15),0px 2px 2px 0px rgba(114, 114, 114, 0.15),0px 1px 5px 0px rgba(114, 114, 114, 0.15)',
    '0px 2px 4px -1px rgba(114, 114, 114, 0.15),0px 4px 5px 0px rgba(114, 114, 114, 0.15),0px 1px 10px 0px rgba(114, 114, 114, 0.15)',
    '0px 3px 5px -1px rgba(114, 114, 114, 0.15),0px 5px 8px 0px rgba(114, 114, 114, 0.15),0px 1px 14px 0px rgba(114, 114, 114, 0.15)',
    '0px 3px 5px -1px rgba(114, 114, 114, 0.15),0px 6px 10px 0px rgba(114, 114, 114, 0.15),0px 1px 18px 0px rgba(114, 114, 114, 0.15)',
    '0px 4px 5px -2px rgba(114, 114, 114, 0.15),0px 7px 10px 1px rgba(114, 114, 114, 0.15),0px 2px 16px 1px rgba(114, 114, 114, 0.15)',
    '0px 5px 5px -3px rgba(114, 114, 114, 0.15),0px 8px 10px 1px rgba(114, 114, 114, 0.15),0px 3px 14px 2px rgba(114, 114, 114, 0.15)',
    '0px 5px 6px -3px rgba(114, 114, 114, 0.15),0px 9px 12px 1px rgba(114, 114, 114, 0.15),0px 3px 16px 2px rgba(114, 114, 114, 0.15)',
    '0px 6px 6px -3px rgba(114, 114, 114, 0.15),0px 10px 14px 1px rgba(114, 114, 114, 0.15),0px 4px 18px 3px rgba(114, 114, 114, 0.15)',
    '0px 6px 7px -4px rgba(114, 114, 114, 0.15),0px 11px 15px 1px rgba(114, 114, 114, 0.15),0px 4px 20px 3px rgba(114, 114, 114, 0.15)',
    '0px 7px 8px -4px rgba(114, 114, 114, 0.15),0px 12px 17px 2px rgba(114, 114, 114, 0.15),0px 5px 22px 4px rgba(114, 114, 114, 0.15)',
    '0px 7px 8px -4px rgba(114, 114, 114, 0.15),0px 13px 19px 2px rgba(114, 114, 114, 0.15),0px 5px 24px 4px rgba(114, 114, 114, 0.15)',
    '0px 7px 9px -4px rgba(114, 114, 114, 0.15),0px 14px 21px 2px rgba(114, 114, 114, 0.15),0px 5px 26px 4px rgba(114, 114, 114, 0.15)',
    '0px 8px 9px -5px rgba(114, 114, 114, 0.15),0px 15px 22px 2px rgba(114, 114, 114, 0.15),0px 6px 28px 5px rgba(114, 114, 114, 0.15)',
    '0px 8px 10px -5px rgba(114, 114, 114, 0.15),0px 16px 24px 2px rgba(114, 114, 114, 0.15),0px 6px 30px 5px rgba(114, 114, 114, 0.15)',
    '0px 8px 11px -5px rgba(114, 114, 114, 0.15),0px 17px 26px 2px rgba(114, 114, 114, 0.15),0px 6px 32px 5px rgba(114, 114, 114, 0.15)',
    '0px 9px 11px -5px rgba(114, 114, 114, 0.15),0px 18px 28px 2px rgba(114, 114, 114, 0.15),0px 7px 34px 6px rgba(114, 114, 114, 0.15)',
    '0px 9px 12px -6px rgba(114, 114, 114, 0.15),0px 19px 29px 2px rgba(114, 114, 114, 0.15),0px 7px 36px 6px rgba(114, 114, 114, 0.15)',
    '0px 10px 13px -6px rgba(114, 114, 114, 0.15),0px 20px 31px 3px rgba(114, 114, 114, 0.15),0px 8px 38px 7px rgba(114, 114, 114, 0.15)',
    '0px 10px 13px -6px rgba(114, 114, 114, 0.15),0px 21px 33px 3px rgba(114, 114, 114, 0.15),0px 8px 40px 7px rgba(114, 114, 114, 0.15)',
    '0px 10px 14px -6px rgba(114, 114, 114, 0.15),0px 22px 35px 3px rgba(114, 114, 114, 0.15),0px 8px 42px 7px rgba(114, 114, 114, 0.15)',
    '0px 11px 14px -7px rgba(114, 114, 114, 0.15),0px 23px 36px 3px rgba(114, 114, 114, 0.15),0px 9px 44px 8px rgba(114, 114, 114, 0.15)',
    '0 1px 3px #00000026' // shadow which is in use for all cards
  ],
  spacing: 4,
  typography: {
    a1: {
      fontSize: '3rem',
      fontWeight: 300,
      lineHeight: 1.2
    },
    a2: {
      fontSize: '3rem',
      fontWeight: 400,
      lineHeight: 1.2
    },
    allVariants: {
      marginBottom: '1rem'
    },
    b1: {
      fontSize: '2.5rem',
      fontWeight: 300,
      letterSpacing: '0.025rem',
      lineHeight: 1.15
    },
    b2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      letterSpacing: '0.025rem',
      lineHeight: 1.15
    },
    c1: {
      fontSize: '2.0625rem',
      fontWeight: 400,
      letterSpacing: '0.02063rem',
      lineHeight: 1.15
    },
    c2: {
      fontSize: '2.0625rem',
      fontWeight: 600,
      letterSpacing: '0.02063rem',
      lineHeight: 1.15
    },
    d1: {
      fontSize: '1.6875rem',
      fontWeight: 400,
      letterSpacing: '0.01688rem',
      lineHeight: 1.15
    },
    d2: {
      fontSize: '1.6875rem',
      fontWeight: 700,
      letterSpacing: '0.01688rem',
      lineHeight: 1.15
    },
    e1: {
      fontSize: '1.5rem',
      fontWeight: 400,
      letterSpacing: '0.015rem',
      lineHeight: 1.25
    },
    e2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '0.015rem',
      lineHeight: 1.25
    },
    f1: {
      fontSize: '1.1875rem',
      fontWeight: 400,
      letterSpacing: '0.01188rem',
      lineHeight: 1.4
    },
    f2: {
      fontSize: '1.1875rem',
      fontWeight: 700,
      letterSpacing: '0.01188rem',
      lineHeight: 1.4
    },
    fontFamily: [
      'Nunito Sans',
      'ui-sans-serif',
      'system-ui',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'Noto Sans,sans-serif',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'Noto Color Emoji'
    ].join(','),
    fontSize: 13,
    g1: {
      fontSize: '1rem',
      fontWeight: 400,
      letterSpacing: '0.01rem',
      lineHeight: 1.36
    },
    g2: {
      fontSize: '1rem',
      fontWeight: 700,
      letterSpacing: '0.0075rem',
      lineHeight: 1.36
    },
    htmlFontSize: 13,
    i1: {
      fontFamily: 'Nunito Sans',
      fontSize: '0.875rem',
      fontWeight: 400,
      letterSpacing: '0.00875rem',
      lineHeight: 1.4
    },
    i2: {
      fontFamily: 'Nunito Sans',
      fontSize: '0.875rem',
      fontWeight: 700,
      letterSpacing: '0.00875rem',
      lineHeight: 1.4
    },
    j1: {
      fontFamily: 'Nunito Sans',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.0075rem',
      lineHeight: 1.4
    },
    j2: {
      fontFamily: 'Nunito Sans',
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.015rem',
      lineHeight: 1.4
    },
    k3: {
      fontSize: '0.6875rem',
      fontWeight: 800,
      letterSpacing: '0.055rem',
      lineHeight: 1.45,
      textTransform: 'uppercase'
    }
  }
} as ThemeOptions);

export const MaterialThemeProvider: FC<MaterialThemeProviderProps> = memo(
  ({ themeOverrides = {}, ...props }) => {
    const whitelabel = usePortalMiddlewareGetWhiteLabelUnauthorized({
      query: {
        enabled: !!props.isWhitelabeled,
        keepPreviousData: true,
        useErrorBoundary: false
      }
    });

    const muiTheme = useMemo(
      () => ({
        ...merge(
          theme,
          !!props.isWhitelabeled && whitelabel.data?.theme
            ? whitelabel.data?.theme
            : themeOverrides
        )
      }),
      [whitelabel.data?.theme, props.isWhitelabeled, themeOverrides]
    );

    return <ThemeProvider theme={muiTheme}>{props.children}</ThemeProvider>;
  }
);

MaterialThemeProvider.displayName = 'MaterialThemeProvider';
